import { Button, Checkbox, Modal, MyIcon, useToastMessage } from 'ccs-ui-components';
import { Modal as BSModal } from 'react-bootstrap';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useLoader from '../../../../common/hooks/Loader';
import { getEventSchema } from '../../../store/_api';
import { extractPayloadFields, generateExpression, newLineRegex, prepareExpressionFromStr } from '../../expression-builder/utilities/Helpers';
import { Expression, ExpressionBuilderData, ExpressionTokenType, branchValues, conditionValues, operatorDisplayMap } from '../../expression-builder/utilities/TypeDefs';

import classes from './ExpressionBuilderSidebar.module.scss';
import EventSchema from '../../event-schema/EventSchema';
import { EventSchemaDetail } from '../../../store/_api/TypeDefs';
import { EVENT_ROUTE } from '../../../../control-plane/constants';

interface ExpressionBuilderSidebarProps {
  onClose: () => void;
  expressionData?: ExpressionBuilderData;
  readOnly?: boolean;
  eventSchema?: EventSchemaDetail;
  onExpressionsUpdated: (expressions: ExpressionBuilderData) => void;
}

const ExpressionBuilderSidebar: React.FC<ExpressionBuilderSidebarProps> = ({ onClose, expressionData = undefined, readOnly = false, eventSchema = undefined, onExpressionsUpdated }: ExpressionBuilderSidebarProps) => {
  const loader = useLoader();
  const toastMessage = useToastMessage();
  const { t: translate } = useTranslation();

  const [expressionText, setExpressionText] = useState('');
  const [currentTokenType, setCurrentTokenType] = useState<ExpressionTokenType | undefined>(ExpressionTokenType.BRANCH);
  const [expression, setExpression] = useState<Expression>([]);
  const [removedExpression, setRemovedExpression] = useState<Expression>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState(0);
  const [fields, setFields] = useState<string[]>([]);
  const [fieldTypes, setFieldTypes] = useState<{ [key: string]: string }>({});
  const [operatorValues, setOperatorValues] = useState<string[]>([]);
  const [lastTokenValue, setLastTokenValue] = useState('');
  const [unprocessedText, setUnprocessedText] = useState('');
  const [rawExpression, setRawExpression] = useState('');
  const [rawExprChecked, setRawExprChecked] = useState(false);
  const [showRawExprSwitchWarn, setShowRawExprSwitchWarn] = useState(false);
  const [sensitiveFields, setSensitiveFields] = useState<string[]>([]);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const isExpressionValid = useMemo(() => (
    (expression.length === 0 || expression[expression.length - 1].type === ExpressionTokenType.VALUE)
    && unprocessedText.length === 0
  ), [expression, unprocessedText]);
  const filteredSuggestions = useMemo(() => {
    const tokenVal = lastTokenValue.trim().toLowerCase();
    let filtered = suggestions;
    if (currentTokenType === ExpressionTokenType.FIELD || currentTokenType === ExpressionTokenType.VALUE) {
      filtered = suggestions.filter((s) => !sensitiveFields.some((sf) => s === sf || s.includes(`.${sf}`) || s.startsWith(`${sf}.`)));
    }
    if (tokenVal) {
      const subset = filtered.filter((s) => s.toLowerCase().includes(tokenVal));
      return (subset.length > 0 || currentTokenType === ExpressionTokenType.VALUE) ? subset : filtered;
    }
    return filtered;
  }, [suggestions, lastTokenValue, currentTokenType, sensitiveFields]);
  const operatorDisplayValues = useMemo(() => operatorValues.map((o) => operatorDisplayMap[o] ?? o), [operatorValues]);

  const expressionInputRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const expChange = (text: string, cursorInMiddle = false) => {
    const { expText, expression: newExpr, nextTokenType, currTokenType, lastTokenVal, operatorValues: ov, processedCount } = prepareExpressionFromStr({ text, fields, fieldTypes });
    setExpression(newExpr);
    if (expText.endsWith(' ')) {
      setCurrentTokenType(nextTokenType);
    } else if (expText.length < expressionText.length) {
      setCurrentTokenType(currTokenType);
    }
    setExpressionText(expText);
    setOperatorValues(ov);
    setLastTokenValue(lastTokenVal);
    setUnprocessedText(expText.substring(processedCount).trim());
    setRawExpression(generateExpression(newExpr, fieldTypes));

    if (cursorInMiddle) {
      if (removedExpression.length === 0) {
        setRemovedExpression(expression.slice(newExpr.length + 1));
      }
      if (expText.endsWith(' ')) {
        setCurrentTokenType(nextTokenType);
      } else if (expText.length < expressionText.length) {
        setCurrentTokenType(currTokenType);
      }
    } else {
      setRemovedExpression([]);
    }
  };

  const expChangeHandler = ({ currentTarget }: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUnsavedChanges(true);
    const text = currentTarget.value.replace(newLineRegex, '').trimStart();
    const cursorPos = currentTarget.selectionStart ?? 0;
    setTimeout(() => {
      currentTarget.selectionStart = cursorPos;
      currentTarget.selectionEnd = cursorPos;
    }, 10);
    expChange(text, cursorPos < text.length);
  };

  const expressionBlurHandler = () => {
    if (expressionInputRef.current !== document.activeElement) {
      setShowSuggestions(false);
    }
  };

  const expKeyHandler = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (ev.key === 'ArrowDown') {
      setSelectedSuggestion((v) => (v + 1) % filteredSuggestions.length);
      ev.preventDefault();
    } else if (ev.key === 'ArrowUp') {
      setSelectedSuggestion((v) => (v - 1 + filteredSuggestions.length) % filteredSuggestions.length);
      ev.preventDefault();
    } else if (ev.key === 'Enter') {
      if (showSuggestions && filteredSuggestions.length > 0) {
        suggestionSelectHandler(filteredSuggestions[selectedSuggestion]);
      }
      ev.preventDefault();
    } else if (ev.key === 'Escape') {
      setShowSuggestions(false);
      ev.preventDefault();
      ev.stopPropagation();
    } else if (ev.key === ' ' && expressionInputRef.current && expressionText[expressionInputRef.current.selectionStart - 1] === ' ') {
      ev.preventDefault();
    }
  };

  const suggestionSelectHandler = (value: string) => {
    let suggestion = value;
    if (currentTokenType === ExpressionTokenType.OPERATOR) {
      suggestion = operatorValues[operatorDisplayValues.indexOf(value) ?? 0];
    }
    const currExpression = [...expression];
    if (currExpression.length > 0 && currExpression[currExpression.length - 1].type === currentTokenType) {
      currExpression.pop();
    }
    let oldText = currExpression.map((e) => e.value).join(' ');
    currExpression.push({ type: currentTokenType ?? ExpressionTokenType.BRANCH, value: suggestion });
    if (oldText.length > 0) {
      oldText = `${oldText} `;
    }
    let restText = removedExpression.map(((e) => e.value)).join(' ');
    if (restText.length > 0) {
      restText = `${restText} `;
    }
    const newText = `${oldText}${suggestion} ${restText}`;
    setExpressionText(newText);
    expChange(newText);
    setUnsavedChanges(true);
    if (expressionInputRef.current) {
      expressionInputRef.current.focus();
      if (restText) {
        setTimeout(() => {
          if (expressionInputRef.current) {
            expressionInputRef.current.selectionStart = newText.length - restText.length - 1;
            expressionInputRef.current.selectionEnd = newText.length - restText.length - 1;
          }
        }, 100);
        setCurrentTokenType(undefined);
        setRemovedExpression([]);
      }
    }
  };

  const clear = () => {
    setExpression([]);
    setExpressionText('');
    setSuggestions([]);
    setSensitiveFields([]);
  };

  const doneClickHandler = () => {
    if (!expressionData) {
      return;
    }
    const expressionBuilderStr = expression.map((ee) => ee.value).join(' ');
    onExpressionsUpdated({
      expressionStr: rawExpression.length > 0 ? rawExpression : undefined,
      expressionBuilderStr: expressionBuilderStr.length > 0 ? expressionBuilderStr : undefined,
      event: expressionData.event,
      toNodeId: expressionData.toNodeId,
      eventVersion: expressionData.eventVersion,
      fromNodeId: expressionData.fromNodeId,
    });
    clear();
    onClose();
  };

  const hideUnsavedChangesModal = () => {
    setShowUnsavedChangesModal(false);
  };

  const onCloseHandler = () => {
    if (unsavedChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      onClose();
    }
  };

  const rawExpChangeHandler = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
    setRawExpression(ev.currentTarget.value.replaceAll(newLineRegex, ''));
    setUnsavedChanges(true);
  };

  const rawExprCheckChangeHandler = (ev: ChangeEvent<HTMLInputElement>) => {
    const { checked } = ev.currentTarget;
    if (!checked && !readOnly && rawExpression.length > 0) {
      setShowRawExprSwitchWarn(true);
      return;
    }
    setRawExprChecked(checked);
  };

  useEffect(() => {
    if (currentTokenType === ExpressionTokenType.BRANCH) {
      setSuggestions(branchValues);
    } else if (currentTokenType === ExpressionTokenType.FIELD || currentTokenType === ExpressionTokenType.VALUE) {
      setSuggestions([...fields].sort((a, b) => (a.charCodeAt(0) - b.charCodeAt(0))));
    } else if (currentTokenType === ExpressionTokenType.CONDITION) {
      setSuggestions(conditionValues);
    } else if (currentTokenType === ExpressionTokenType.OPERATOR) {
      setSuggestions(operatorDisplayValues);
    } else {
      setSuggestions([]);
    }
    setSelectedSuggestion(0);
    if (expressionText.length > 0) {
      setShowSuggestions(true);
    }
  }, [currentTokenType]);

  useEffect(() => {
    suggestionsRef.current?.children[selectedSuggestion]?.scrollIntoView(false);
  }, [selectedSuggestion]);

  const handleEventSchema = async (expressionBuilderStr: string) => {
    if (!expressionData) {
      return;
    }
    loader.show();
    const fieldType: { [key: string]: string } = {};
    const fieldsLocal: string[] = [];
    const eventSchemaResponse = await getEventSchema(expressionData.event, expressionData.eventVersion);
    if (eventSchemaResponse) {
      setSensitiveFields(eventSchemaResponse.sensitiveFields || []);
      const parsedPayloadData = JSON.parse(eventSchemaResponse.schema);
      let fieldToParse = parsedPayloadData.fields[0].type;
      if (Array.isArray(fieldToParse)) {
        fieldToParse = fieldToParse[1].fields;
      } else {
        fieldToParse = fieldToParse.fields;
      }
      const payloadFields = extractPayloadFields(fieldToParse);
      payloadFields.forEach((f) => {
        if (!fieldsLocal.includes(f.field)) {
          fieldsLocal.push(f.field);
        }
        fieldType[f.field] = f.type;
      });
    }
    setFields([...fieldsLocal].sort((a, b) => b.length - a.length));
    setFieldTypes(fieldType);
    loader.hide();

    if (fieldsLocal.length > 0) {
      const { expression: parsedExpression, processedCount } = prepareExpressionFromStr({ text: expressionBuilderStr, fields: fieldsLocal, fieldTypes: fieldType });
      setExpression(parsedExpression);
      setUnprocessedText(expressionBuilderStr.substring(processedCount).trim());
    } else {
      setUnprocessedText(expressionBuilderStr);
      toastMessage.failure(translate('expression_builder_component.title_toast_error'), translate('expression_builder_component.msg_toast_body_error'));
    }
  };

  useEffect(() => {
    if (!expressionData) {
      return;
    }
    const expressionBuilderStr = expressionData.expressionBuilderStr ?? '';
    setExpressionText(expressionBuilderStr);
    setExpression([]);
    setUnprocessedText('');
    let sugg: string[] = [];
    if (expressionBuilderStr.length === 0) {
      setCurrentTokenType(ExpressionTokenType.BRANCH);
      sugg = branchValues;
    }
    setSuggestions(sugg);
    setRawExpression(expressionData.expressionStr ?? '');
    setRawExprChecked(!expressionData.expressionBuilderStr && !!expressionData.expressionStr);
    handleEventSchema(expressionBuilderStr);
  }, [expressionData]);

  const eventTitle = eventSchema && `${eventSchema.eventName}  (${translate('expression_builder_component.txt_version', { version: eventSchema.version })})`;

  const getEventUrl = () => {
    if (eventSchema) {
      return `${EVENT_ROUTE.EVENTS_VIEW_ROUTE}${eventSchema.eventName}/${eventSchema.version}`;
    }
    return '';
  };

  return (
    <>
      <div className={classes['sidebar-heading']} data-testid="expressionBuilderSideBarHeader">
        <div className={classes['close-sidebar']}>
          <div className={classes['sidebar-title']} data-testid="sidebarTitle">
            {translate('step_elements.txt_conditional_exp')}{' '}
            {eventSchema ? (
              <a
                href={getEventUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className={classes['event-link']}
                data-testid="expressionBuilderEventLink"
                title={`View ${eventSchema.eventName} details in new tab`}
              >
                {eventTitle}
                <i className="fa-solid fa-arrow-up-right-from-square" />
              </a>
            ) : (
              <span>{eventTitle}</span>
            )}
          </div>
          <Button variant="tertiary" data-testid="sidebarCloseBtn" onClick={onCloseHandler}>
            <i className="fa-solid fa-xmark" />
          </Button>
        </div>
      </div>

      {toastMessage.renderToast()}
      <div className={classes['expression-builder']}>
        {loader.render()}
        <div className={classes['expression-row']}>
          <div className="form-check form-switch">
            <Checkbox checked={rawExprChecked} role="switch" label={String(translate('expression_builder_component.lbl_checkbox'))} onChange={rawExprCheckChangeHandler} data-testid="checkRawExpression" />
          </div>
          {!rawExprChecked && (
            <div className={classes['input-wrapper']}>
              <div className={classes.textarea}>
                <div className={classes.hidden}>{expressionText}</div>
                <textarea
                  placeholder={String(translate('expression_builder_component.txt_textarea_placeholder'))}
                  onKeyDown={expKeyHandler}
                  value={expressionText}
                  onChange={expChangeHandler}
                  onFocus={() => setShowSuggestions(true)}
                  onClick={() => setShowSuggestions(true)}
                  onBlur={expressionBlurHandler}
                  ref={expressionInputRef}
                  data-testid="rawExpDefaultTextField"
                  disabled={readOnly}
                />
                <div className={classes['token-wrapper']}>
                  {expression.map((e, i) => ({ ...e, k: i })).map((e) => (
                    <span className={classes[e.type.toLowerCase()]} key={e.k}>{e.value}</span>
                  ))}
                  {unprocessedText.split(' ').map((t, i) => ({ d: t, k: i })).map((t) => <span key={t.k}>{t.d}</span>)}
                </div>
              </div>
              <Button className={classes.action} variant="text" disabled={!isExpressionValid || readOnly} onClick={doneClickHandler} data-testid="submitExpIcon" title={String(translate('expression_builder_component.btn_save_expression'))}>
                <MyIcon name="checkCircleFillIcon" />
              </Button>
            </div>
          )}
          {rawExprChecked && (
            <div className={classes['input-wrapper']}>
              <textarea
                value={rawExpression}
                onChange={rawExpChangeHandler}
                data-testid="rawExpCheckedTextField"
                aria-label={String(translate('expression_builder_component.lbl_checkbox'))}
                disabled={readOnly}
              />
              <Button className={classes.action} variant="text" onClick={doneClickHandler} data-testid="rawExpCheckedIcon" disabled={readOnly} title={String(translate('expression_builder_component.btn_save_expression'))}>
                <MyIcon name="largeCheckIcon" />
              </Button>
            </div>
          )}
        </div>
        {!rawExprChecked && showSuggestions && filteredSuggestions.length > 0 && !readOnly && (
          <div className={classes.suggestions} ref={suggestionsRef} data-testid="showSuggestions">
            {filteredSuggestions.map((s, idx) => (
              <Button className={idx === selectedSuggestion ? classes.selected : ''} variant="text" key={s} onMouseDown={(ev) => ev.preventDefault()} onClick={() => suggestionSelectHandler(s)} data-testid="clickOnSuggestion">{s}</Button>
            ))}
          </div>
        )}
        {eventSchema && (
          <div className="mt-5" data-testid="expressionSidebarEventSchema">
            <EventSchema eventSchema={eventSchema} />
          </div>
        )}
      </div>

      <Modal
        className={classes['warning-modal']}
        modalTitle={String(translate('expression_builder_component.modal_title'))}
        modalBody={String(translate('expression_builder_component.modal_body'))}
        modalShow={showRawExprSwitchWarn}
        btnSaveText={String(translate('expression_builder_component.btn_save_txt'))}
        btnCloseText={String(translate('expression_builder_component.btn_close_txt'))}
        onSaveModal={() => {
          setShowRawExprSwitchWarn(false);
          setRawExprChecked(false);
        }}
        onCloseModal={() => {
          setShowRawExprSwitchWarn(false);
        }}
        alignToCentre
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="warning"
      />

      <BSModal
        onHide={hideUnsavedChangesModal}
        show={showUnsavedChangesModal}
        centered
        className={classes['unsaved-changes-modal']}
      >
        <BSModal.Header closeButton className={classes['modal-header']}>
          <BSModal.Title className={classes['modal-title']}>
            {String(translate('expression_builder_component.modal_title_unsaved_changes'))}
          </BSModal.Title>
        </BSModal.Header>
        <BSModal.Body className={classes['modal-body']}>
          {String(translate('expression_builder_component.modal_body_unsaved_changes'))}
        </BSModal.Body>
        <BSModal.Footer className={classes['modal-footer']}>
          <Button variant="secondary" onClick={hideUnsavedChangesModal}>
            {String(translate('expression_builder_component.btn_cancel_txt'))}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              hideUnsavedChangesModal();
              onClose();
            }}
          >
            {String(translate('expression_builder_component.btn_discard_txt'))}
          </Button>
          <Button
            disabled={!isExpressionValid || readOnly}
            variant="primary"
            onClick={() => {
              hideUnsavedChangesModal();
              doneClickHandler();
            }}
          >
            {String(translate('expression_builder_component.modal_save_unsaved_changes'))}
          </Button>
        </BSModal.Footer>
      </BSModal>

    </>
  );
};

export default ExpressionBuilderSidebar;
